import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchEvents, fetchVenues, type FestivalEvent, type FestivalVenue } from '../utils/festivalApi';
import { usePrerenderData } from '../PrerenderDataContext';
import { useI18n } from '../i18n/useI18n';
import { LandingFooter } from './LandingFooter';
import FestivalHeader from './FestivalHeader';
import FestivalFilters from './FestivalFilters';

function karaokeTypeLabel(type: string, t: (key: string) => string) {
  if (type === 'public') return t('festival.openToPublic');
  if (type === 'private') return t('festival.privateRooms');
  if (type === 'both') return t('festival.publicAndPrivate');
  return t('festival.openToPublic');
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function slugifyCity(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function CityVenuesPage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const prerenderData = usePrerenderData();
  const initialCity = prerenderData.city?.slug === citySlug ? prerenderData.city : undefined;

  const [cityName, setCityName] = useState<string>(initialCity?.name ?? '');
  const [allCityVenues, setAllCityVenues] = useState<FestivalVenue[]>(initialCity?.venues ?? []);
  const [events, setEvents] = useState<FestivalEvent[]>(initialCity?.events ?? []);
  const [filteredVenues, setFilteredVenues] = useState<FestivalVenue[]>(initialCity?.venues ?? []);
  const [cities, setCities] = useState<string[]>(initialCity?.cities ?? []);
  const [loading, setLoading] = useState(!initialCity);
  const [error, setError] = useState<string | null>(null);
  const [karaokeType, setKaraokeType] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>(initialCity?.name ?? '');
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(6);

  // Load data on mount if not provided by prerender.
  useEffect(() => {
    if (initialCity) {
      setFilteredVenues(initialCity.venues);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([fetchVenues(), fetchEvents()])
      .then(([allVenues, allEvents]) => {
        if (cancelled) return;

        const cityVenues = allVenues.filter((v) => slugifyCity(v.city) === citySlug);
        if (cityVenues.length === 0) {
          setError(t('venues.noCityVenues'));
          setLoading(false);
          return;
        }

        const venueIds = new Set(cityVenues.map((v) => v.id));
        const cityEvents = allEvents.filter((e) => venueIds.has(e.venue_id));
        const cityList = Array.from(new Set(allVenues.map((v) => v.city).filter(Boolean))).sort();

        const name = cityVenues[0].city;
        setCityName(name);
        setSearchQuery(name);
        setAllCityVenues(cityVenues);
        setEvents(cityEvents);
        setCities(cityList);
        setFilteredVenues(cityVenues);
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('venues.loadError'));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [citySlug, initialCity]);

  // Apply karaoke type and search filters within the city.
  useEffect(() => {
    let list = allCityVenues;

    if (karaokeType) {
      list = list.filter((v) => v.karaoke_type === karaokeType);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q && q !== cityName.toLowerCase()) {
      list = list.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.address?.toLowerCase().includes(q) ||
          v.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }

    setFilteredVenues(list);
    setVisibleCount(6);
  }, [allCityVenues, karaokeType, searchQuery, cityName]);

  const autocompleteCities = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return cities.slice(0, 8);
    return cities.filter((c) => c.toLowerCase().includes(q)).slice(0, 8);
  }, [cities, searchQuery]);

  const eventsByVenue = useMemo(() => {
    const map = new Map<string, FestivalEvent[]>();
    for (const event of events) {
      const list = map.get(event.venue_id) || [];
      list.push(event);
      map.set(event.venue_id, list);
    }
    return map;
  }, [events]);

  const visibleVenues = useMemo(() => filteredVenues.slice(0, visibleCount), [filteredVenues, visibleCount]);

  const handleCitySelect = (city: string) => {
    navigate(`/city/${encodeURIComponent(slugifyCity(city))}`);
  };

  const clearSearch = () => {
    setSearchQuery(cityName);
  };

  if (loading) {
    return (
      <div className="festival-page">
        <FestivalHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          autocompleteCities={autocompleteCities}
          onCitySelect={handleCitySelect}
          onClearSearch={clearSearch}
        />
        <main className="festival-main festival-main--centered">
          <p className="festival-loading">{t('venues.loading')}</p>
        </main>
        <LandingFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="festival-page">
        <FestivalHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          autocompleteCities={autocompleteCities}
          onCitySelect={handleCitySelect}
          onClearSearch={clearSearch}
        />
        <main className="festival-main festival-main--centered">
          <p className="festival-error">{error}</p>
          <Link to="/venues" className="festival-error__link">{t('venues.viewAll')}</Link>
        </main>
        <LandingFooter />
      </div>
    );
  }

  return (
    <div className="festival-page">
      <FestivalHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        autocompleteCities={autocompleteCities}
        onCitySelect={handleCitySelect}
        onClearSearch={clearSearch}
      />

      <FestivalFilters activeType={karaokeType} onChange={setKaraokeType} />

      <main className="festival-main">
        <section className="festival-list">
          <div className="festival-list__header">
            <h1 className="festival-list__title">{t('venues.cityTitle', { city: cityName })}</h1>
            <p className="festival-list__count">{t('venues.placeCount', { count: filteredVenues.length, suffix: filteredVenues.length !== 1 ? 's' : '' })}</p>
          </div>

          {filteredVenues.length === 0 && (
            <div className="festival-empty">
              <p>{t('venues.noMatchInCity', { city: cityName })}</p>
              <Link to="/venues" className="festival-empty__cta">{t('venues.viewAll')}</Link>
            </div>
          )}

          <div className="festival-cards festival-cards--city">
            {visibleVenues.map((venue) => {
              const venueEvents = eventsByVenue.get(venue.id) || [];
              const nextEvent = venueEvents[0];

              return (
                <article
                  key={venue.id}
                  className={`festival-card ${selectedVenueId === venue.id ? 'festival-card--selected' : ''}`}
                  onClick={() => setSelectedVenueId(venue.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedVenueId(venue.id); }}
                >
                  <div className="festival-card__media">
                    {venue.avatar_url ? (
                      <img src={venue.avatar_url} alt={venue.name} />
                    ) : (
                      <div className="festival-card__placeholder">
                        <span>🎤</span>
                      </div>
                    )}
                    {venue.verified && (
                      <span className="festival-card__badge festival-card__badge--icon" aria-label={t('venues.verified')} title={t('venues.verified')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </div>

                  <div className="festival-card__body">
                    <div className="festival-card__row festival-card__row--title">
                      <h2 className="festival-card__title">{venue.name}</h2>
                      {/*<div className="festival-card__rating">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        <span>{venue.rating ? venue.rating.toFixed(2) : t('venues.new')}</span>
                      </div>*/}
                    </div>

                    <p className="festival-card__subtitle">
                      {venue.address || venue.city}
                      <span className="festival-card__dot" />
                      {karaokeTypeLabel(venue.karaoke_type, t)}
                    </p>

                    {venue.bio && <p className="festival-card__desc">{venue.bio}</p>}

                    {nextEvent && (
                      <div className="festival-card__event">
                        <span className="festival-card__event-date">{formatDate(nextEvent.starts_at)}</span>
                        <span className="festival-card__event-title">{nextEvent.title}</span>
                      </div>
                    )}

                    <div className="festival-card__actions">
                      <Link to={`/venues/${encodeURIComponent(venue.slug)}`} className="festival-card__link" onClick={(e) => e.stopPropagation()}>
                        {t('event.viewDetails')}
                      </Link>
                      {venue.google_maps_url && (
                        <a href={venue.google_maps_url} target="_blank" rel="noopener noreferrer" className="festival-card__link festival-card__link--secondary" onClick={(e) => e.stopPropagation()}>
                          {t('event.directions')}
                        </a>
                      )}
                      {venue.website && (
                        <a href={venue.website} target="_blank" rel="noopener noreferrer" className="festival-card__link festival-card__link--secondary" onClick={(e) => e.stopPropagation()}>
                          {t('event.website')}
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {visibleCount < filteredVenues.length && (
            <div className="festival-load-more">
              <button
                type="button"
                className="festival-load-more__btn"
                onClick={() => setVisibleCount((c) => c + 6)}
              >
                {t('venues.loadMore')}
              </button>
            </div>
          )}
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
